import { readonly, ref } from 'vue'
import {
	decodePostings,
	queryTokens,
	tokenizeSearch,
	type SearchIndexArticle,
	type SearchIndexAuthor,
	type SearchIndexFile,
	type SearchIndexTopic,
} from '@/utils/search'

export interface ArticleSearchResult {
	article: SearchIndexArticle
	link: string
	authors: SearchIndexAuthor[]
	topics: SearchIndexTopic[]
	score: number
}

export interface SearchResults {
	articles: ArticleSearchResult[]
	authors: SearchIndexAuthor[]
	topics: SearchIndexTopic[]
}

const EMPTY_RESULTS: SearchResults = { articles: [], authors: [], topics: [] }

const INDEX_URL = `${import.meta.env.BASE_URL}search-index.json`
const MAX_ARTICLES = 60
const MAX_AUTHORS = 20
const MAX_TOPICS = 20

interface PreparedIndex {
	raw: SearchIndexFile
	keys: string[]
	authorBySlug: Map<string, SearchIndexAuthor>
	topicBySlug: Map<string, SearchIndexTopic>
}

let indexPromise: Promise<SearchIndexFile> | null = null
let prepared: PreparedIndex | null = null

function prepare(raw: SearchIndexFile): PreparedIndex {
	const authorBySlug = new Map<string, SearchIndexAuthor>()
	for (const author of raw.authors) authorBySlug.set(author.s, author)
	const topicBySlug = new Map<string, SearchIndexTopic>()
	for (const topic of raw.topics) topicBySlug.set(topic.s, topic)
	return { raw, keys: Object.keys(raw.index).sort(), authorBySlug, topicBySlug }
}

async function loadIndexFile(): Promise<SearchIndexFile> {
	indexPromise ??= fetch(INDEX_URL, { headers: { accept: 'application/json' } })
		.then((response) => {
			if (!response.ok) throw new Error(`Search index unavailable (${response.status})`)
			return response.json() as Promise<SearchIndexFile>
		})
		.catch((error) => {
			// Allow a later retry instead of caching a failed load forever.
			indexPromise = null
			throw error
		})
	return indexPromise
}

/** First position in `keys` that is >= `value`. */
function lowerBound(keys: string[], value: string): number {
	let low = 0
	let high = keys.length
	while (low < high) {
		const middle = (low + high) >>> 1
		if (keys[middle] < value) low = middle + 1
		else high = middle
	}
	return low
}

/** Article positions for an exact token or any index token starting with it. */
function postingsForToken(index: PreparedIndex, term: string): Set<number> {
	const positions = new Set<number>()
	let cursor = lowerBound(index.keys, term)
	while (cursor < index.keys.length && index.keys[cursor].startsWith(term)) {
		for (const position of decodePostings(index.raw.index[index.keys[cursor]])) {
			positions.add(position)
		}
		cursor += 1
	}
	return positions
}

function matchesAllTokens(haystack: string, tokens: string[]): boolean {
	const value = haystack.toLowerCase()
	return tokens.every(token => value.includes(token))
}

function articleLink(article: SearchIndexArticle): string {
	return `/${article.k === 'blog' ? 'blog' : 'article'}/${article.s}`
}

/**
 * Full-text search over the statically generated index.
 *
 * Pieces are matched by prefix over every indexed word, and additionally by the
 * name of any topic or author attached to them, so searching an author surfaces
 * their pieces even when the name never appears in the text.
 */
export function runSearch(index: PreparedIndex, query: string): SearchResults {
	const tokens = queryTokens(query)
	if (!tokens.length) return EMPTY_RESULTS

	const matchedAuthors = index.raw.authors.filter(author => matchesAllTokens(author.n, tokens))
	const matchedTopics = index.raw.topics.filter(topic => matchesAllTokens(topic.n, tokens))

	const authorSlugs = new Set(matchedAuthors.map(author => author.s))
	const topicSlugs = new Set(matchedTopics.map(topic => topic.s))

	// Intersect token postings (all words must appear somewhere in the piece).
	let positions: Set<number> | null = null
	for (const token of tokens) {
		const found = postingsForToken(index, token)
		if (!positions) positions = found
		else positions = new Set([...positions].filter(position => found.has(position)))
		if (!positions.size) break
	}

	const candidates = new Set<number>(positions ?? [])
	if (authorSlugs.size || topicSlugs.size) {
		index.raw.articles.forEach((article, position) => {
			if (candidates.has(position)) return
			if (article.a?.some(slug => authorSlugs.has(slug))) candidates.add(position)
			else if (article.p?.some(slug => topicSlugs.has(slug))) candidates.add(position)
		})
	}

	const results: ArticleSearchResult[] = []
	for (const position of candidates) {
		const article = index.raw.articles[position]
		if (!article) continue
		const title = article.t.toLowerCase()
		const teaser = (article.e ?? '').toLowerCase()
		const authors = (article.a ?? []).flatMap(slug => {
			const author = index.authorBySlug.get(slug)
			return author ? [author] : []
		})
		const topics = (article.p ?? []).flatMap(slug => {
			const topic = index.topicBySlug.get(slug)
			return topic ? [topic] : []
		})

		let score = 0
		if (tokens.every(token => title.includes(token))) score += 100
		else if (tokens.some(token => title.includes(token))) score += 30
		if (tokens.some(token => teaser.includes(token))) score += 8
		if (authors.some(author => matchesAllTokens(author.n, tokens))) score += 60
		if (topics.some(topic => matchesAllTokens(topic.n, tokens))) score += 20
		if (!score) score += 1

		results.push({ article, link: articleLink(article), authors, topics, score })
	}

	results.sort((a, b) => b.score - a.score || (b.article.d ?? '').localeCompare(a.article.d ?? ''))

	return {
		articles: results.slice(0, MAX_ARTICLES),
		authors: matchedAuthors.slice(0, MAX_AUTHORS),
		topics: matchedTopics.slice(0, MAX_TOPICS),
	}
}

export function useSearchIndex() {
	const loading = ref(false)
	const error = ref<string | null>(null)
	const ready = ref(prepared !== null)

	async function load(): Promise<void> {
		if (prepared) {
			ready.value = true
			return
		}
		loading.value = true
		error.value = null
		try {
			prepared = prepare(await loadIndexFile())
			ready.value = true
		} catch (loadError) {
			error.value = loadError instanceof Error ? loadError.message : 'Search is unavailable.'
		} finally {
			loading.value = false
		}
	}

	function search(query: string): SearchResults {
		if (!prepared || !query.trim()) return EMPTY_RESULTS
		return runSearch(prepared, query)
	}

	/** Debug helper for the SSR/preview console. */
	function tokenize(query: string): string[] {
		return tokenizeSearch(query)
	}

	return {
		load,
		search,
		tokenize,
		loading: readonly(loading),
		error: readonly(error),
		ready: readonly(ready),
	}
}
